/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Else, If, Then, When } from "react-if";
import { Text, theme } from "@nordcloud/gnui";
import {
  ChartType,
  SearchSortField,
  SearchSortOrder,
  SearchV2Filter,
} from "~/generated/graphql";
import {
  DateRange,
  ErrorWrap,
  EstatePeriod,
  Granularity,
  LoaderWrap,
  Pagination,
  ReactTable,
  SortDirection,
} from "~/components";
import { ERROR_COLORS } from "~/constants";
import {
  estateV2FilterFields,
  useCurrency,
  useEstateRecordsV2,
  useEstateSortV2,
  useFilteredQueryState,
} from "~/hooks";
import { showError } from "~/services/toast";
import { isEmpty, isNotEmpty } from "~/tools";
import {
  EstateRecordsLoader,
  FiltersProp,
  ParametersBar,
  BillingPeriod,
  EstateRecordsAccumulatedCostChart,
  getEndDate,
  getStartDate,
  useEstateChartParameters,
  useEstateRecordsChartJob,
} from "./components";
import { EstateListColumns } from "./EstateList";
import { convertTags, generateNewRowData, getMultiResources } from "./helpers";

type Props = {
  period: EstatePeriod;
  range: DateRange | undefined;
  granularity: Granularity;
  setIsDataLoading: (loading: boolean) => void;
  envNid?: string;
};

type SortingProp = {
  field?: SearchSortField;
  order?: SearchSortOrder;
};

type EstateQueryState = FiltersProp &
  SearchV2Filter &
  SortingProp & {
    page: number;
    limit: number;
    query: string;
  };

export function EstateRecordsAccumulatedView({
  envNid,
  period,
  range,
  granularity,
  setIsDataLoading,
}: Props) {
  const { search, setSearch } = useEstateChartParameters();
  const { currency } = useCurrency();
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const { state, updateQueryState } =
    useFilteredQueryState(estateV2FilterFields);

  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const { page, limit, query, field, order, ...restFilters }: EstateQueryState =
    state;

  const { sortField, sortDirection, onSortSelect, onSortOrderChange } =
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    useEstateSortV2({ field, order, updateQueryState });

  const billingPeriod = getBillingPeriod(period);
  const isEstateRecordsVisible = period !== EstatePeriod.RANGE;

  const estateFilters = {
    filter: {
      ...restFilters,
      metadata: convertTags(restFilters.metadata),
      tags: convertTags(restFilters.tags),
      billingPeriod,
    },
    query,
    sort: {
      field: sortField,
      order: sortDirection,
    },
  };

  const { data, loading, error, refetch } = useEstateRecordsV2(
    {
      variables: {
        page,
        limit,
        ...estateFilters,
      },
      skip: !isEstateRecordsVisible,
    },
    showError
  );

  const rows = useMemo(
    () =>
      data?.resources?.map((res) => ({
        ...generateNewRowData(res, currency, envNid),
      })) ?? [],
    [data, currency, envNid]
  );

  const hasResources = isNotEmpty(rows);
  const estateCount = data?.count ?? 0;
  const showPagination = estateCount > 0;

  const columns = useMemo(() => EstateListColumns, []);

  const resources = useMemo(
    () => getMultiResources(selectedResources, data?.resources),
    [selectedResources, data?.resources]
  );

  const selectedRowsHandler = useCallback(
    (items: string[]) => {
      setSelectedResources(
        items.filter((item) =>
          data?.resources
            ?.filter((row) => row.__typename !== "CostResource")
            .map((row) => row.id)
            .includes(item)
        )
      );
    },
    [setSelectedResources, data?.resources]
  );

  const startDate = getStartDate(period, range);
  const endDate = getEndDate(period, range);

  const {
    chartData,
    loading: isChartLoading,
    error: chartError,
  } = useEstateRecordsChartJob(
    {
      variables: {
        filter: restFilters,
        startDate,
        endDate,
        chartType: ChartType.Accumulated,
      },
    },
    showError
  );

  useEffect(() => {
    setIsDataLoading(loading || isChartLoading);
  }, [loading, isChartLoading, setIsDataLoading]);

  const sortOrder =
    sortDirection === SearchSortOrder.Asc
      ? SortDirection.Ascending
      : SortDirection.Descending;

  return (
    <div>
      <EstateRecordsAccumulatedCostChart
        startDate={startDate}
        endDate={endDate}
        granularity={granularity}
        period={period}
        range={range}
        loading={isChartLoading}
        isCostLoading={loading}
        chartData={chartData}
        error={chartError}
        currentMonthTotal={data?.cost?.currentMonthTotal}
        monthlySavings={data?.savingsAmount?.total ?? ""}
        estateRecordsCount={data?.count ?? 0}
      />
      <When condition={isEstateRecordsVisible}>
        <ParametersBar
          search={search}
          setSearch={setSearch}
          disabledAttachment={isEmpty(selectedResources)}
          sortField={sortField}
          resources={resources}
          refresh={refetch}
          sortDirection={sortOrder}
          totalRecords={data?.count ?? 0}
          queryFilters={estateFilters}
          disabled={loading || isChartLoading}
          onSortOrderChange={onSortOrderChange}
          onSortSelect={onSortSelect}
        />
        <ErrorWrap
          error={error}
          status={
            hasResources ? ERROR_COLORS.notification : ERROR_COLORS.danger
          }
        >
          <LoaderWrap loading={loading} Component={EstateRecordsLoader}>
            <If condition={hasResources}>
              <Then>
                <ReactTable
                  columns={columns}
                  data={rows}
                  sort={false}
                  headerBackground={theme.color.background.ui03}
                  deselectRows={isEmpty(selectedResources ?? [])}
                  onSelectedRowsChange={selectedRowsHandler}
                />
                <When condition={showPagination}>
                  <Pagination count={estateCount} />
                </When>
              </Then>
              <Else>
                <Text
                  paddingTop={theme.spacing.spacing04}
                  paddingBottom={theme.spacing.spacing04}
                >
                  No resources matching selected criteria
                </Text>
              </Else>
            </If>
          </LoaderWrap>
        </ErrorWrap>
      </When>
    </div>
  );
}

function getBillingPeriod(period: EstatePeriod): BillingPeriod | undefined {
  switch (period) {
    case EstatePeriod.CURRENT_MONTH:
      return BillingPeriod.Current;
    case EstatePeriod.PREVIOUS_MONTH:
      return BillingPeriod.Previous;
    default:
      return undefined;
  }
}
